import { useState } from "react";
import { Pencil, AlertTriangle, Eye, EyeOff, Printer } from "lucide-react";
import StepShell from "./StepShell";
import { fmtMoney } from "../../config";
import {
  computeOhioTax, computeMonthlyPayment,
  FEE_DOC, FEE_STATE, FEE_TITLE,
  CREDIT_TIERS, OHIO_COUNTIES,
} from "../../data/finance";

/**
 * Step 8 — Review.
 *
 * Final summary before submission. Each section has an Edit button that
 * sends the customer back to that step with every prior answer still
 * intact. The math here READS finance.js constants and helpers — it
 * does NOT introduce new tax/fee/rebate logic.
 *
 * Defaults that match the existing Build-A-Deal:
 *   • County: Franklin (Ohio default per Scott's spec)
 *   • Credit tier: "good"
 *   • Term: 72 months unless Special APR offer overrides
 */
export default function ReviewStep({ byod, vehicle, onContinue, onBack, onEdit, firstName }) {
  const s = byod.state;
  const price = Number(vehicle?.price || 0);

  // Resolve trade: use selected value (low/mid/high) when has_trade === "yes"
  const tradeValue = s.trade?.has_trade === "yes" ? byod.tradeSelectedValue : 0;
  const tradePayoff = s.trade?.has_trade === "yes" ? (Number(s.trade?.payoff || 0) || 0) : 0;
  const netTrade = tradeValue - tradePayoff;

  const rebatesTotal = Number(s.rebates?.total_savings_estimated || 0);
  const accessoriesTotal = byod.accessoriesTotal;
  const coveragesTotal = byod.coveragesTotal;
  const down = byod.moneyDownAmount;

  // Tax — uses the county the customer picked in Step 10. Fall back to Franklin.
  const countyName = s.sales_tax?.county || "Franklin";
  const countyRow = OHIO_COUNTIES.find((c) => c.name === countyName) || OHIO_COUNTIES[0];
  const tax = price > 0 ? computeOhioTax({
    price, tradeValue, rebates: rebatesTotal, rate: countyRow.rate,
  }) : 0;
  const fees = FEE_DOC + FEE_STATE + FEE_TITLE;

  const amountFinanced = price > 0
    ? Math.max(0, price + tax + fees + accessoriesTotal + coveragesTotal - down - netTrade - rebatesTotal)
    : 0;

  // Rate — use the customer-picked tier from Step 6, or fall back to "good".
  const tierValue = s.rate?.credit_tier || "good";
  const tier = CREDIT_TIERS.find((t) => t.value === tierValue) || CREDIT_TIERS[1];
  const isNew = (vehicle?.status || "").toLowerCase() === "new";
  const baseRate = isNew ? tier.new : tier.used;
  const aprOverride = s.rate?.special_apr_pct;
  const aprTerm = s.rate?.special_apr_term;
  const termSelected = s.rate?.term || 72;

  // Single estimated monthly payment. If a Special APR is selected we use
  // it directly; otherwise we use the customer-selected credit tier + term.
  // No more "range" — we show ONE payment to keep things crisp, plus the
  // amount financed underneath so the customer can see how it was built.
  let estimatedMonthly = 0;
  let usingDefaults = false;
  let displayApr = 0;          // for the "Based on" line
  let displayTerm = termSelected;
  let displayTierLabel = tier.label;
  if (amountFinanced > 0) {
    if (aprOverride != null && aprTerm) {
      estimatedMonthly = computeMonthlyPayment({ amountFinanced, annualRate: aprOverride / 100, months: aprTerm });
      displayApr = aprOverride;
      displayTerm = aprTerm;
      displayTierLabel = "Special APR";
    } else {
      estimatedMonthly = computeMonthlyPayment({ amountFinanced, annualRate: baseRate, months: termSelected });
      displayApr = baseRate * 100;
      // Customer never explicitly picked a tier/term? Mark as defaults.
      usingDefaults = !s.rate?.credit_tier || s.rate.credit_tier === "good";
    }
  }

  // Disclaimer agreement gate — customer must check the box before the
  // final amount-financed + monthly payment are revealed.
  const [agreed, setAgreed] = useState(!!s.review?.estimate_acknowledged);
  const accept = (v) => {
    setAgreed(v);
    byod.replaceSection("review", { ...(s.review || {}), estimate_acknowledged: v, estimate_acknowledged_at: v ? new Date().toISOString() : null });
  };

  const handleSavePdf = () => {
    // Add a class to the page body so our print stylesheet hides every
    // chrome element (header, nav, chat, footer, action buttons, etc.) and
    // only the print-friendly summary card stays on the page when the
    // browser fires its native Print → Save as PDF dialog.
    if (typeof document !== "undefined") {
      document.body.classList.add("byod-printing");
      // Make sure the estimate is revealed before printing so the customer
      // sees the full deal in the PDF without needing to tick the gate.
      if (!agreed) accept(true);
      setTimeout(() => {
        window.print();
        document.body.classList.remove("byod-printing");
      }, 100);
    }
  };

  return (
    <StepShell
      title={firstName ? `${firstName}, here's everything in one place.` : "Here's everything in one place."}
      subtitle="Tap Edit on any section to make changes — your other answers stay intact. When it looks right, hit Continue to send it to Scott."
      onBack={onBack}
      onContinue={onContinue}
      continueLabel="Continue to submit"
      testIdPrefix="byod-review-step"
      extraActions={
        <button
          type="button"
          onClick={handleSavePdf}
          className="ssc-btn ssc-btn-outline"
          data-testid="byod-review-save-pdf"
        >
          <Printer size={14} /> Save as PDF
        </button>
      }
    >
      <div className="space-y-4 byod-print-area" data-testid="byod-review-print-area">
        <div className="byod-print-header hidden print:block mb-4">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ssc-blue)] font-extrabold">Build My Own Deal — Estimate</div>
          <div className="text-xl font-black text-[var(--ssc-navy)]">{firstName ? `${firstName}'s deal` : "Your deal estimate"}</div>
          <div className="text-xs text-[var(--ssc-ink-2)] mt-0.5">Generated {new Date().toLocaleString()} · Scott Sells Chevys</div>
        </div>
        <Section title="Vehicle" stepKey="vehicle" onEdit={onEdit} testid="byod-review-vehicle">
          {vehicle ? (
            <div className="grid sm:grid-cols-[100px,1fr] gap-3">
              {vehicle.photos?.[0]?.url ? (
                <img src={vehicle.photos[0].url} alt="" className="w-full sm:w-[100px] h-[80px] object-cover rounded-lg" />
              ) : null}
              <div>
                <div className="font-bold text-[var(--ssc-navy)]">{vehicle.year} {vehicle.make} {vehicle.model} {vehicle.trim}</div>
                <div className="text-xs text-[var(--ssc-ink-2)] mt-1">Stock {vehicle.stock_number} · VIN {vehicle.vin}</div>
                <div className="font-extrabold text-[var(--ssc-navy)] mt-1">{fmtMoney(price)}</div>
              </div>
            </div>
          ) : <Empty text="No vehicle selected." />}
        </Section>

        <Section title="Numbers" stepKey="sales_tax" onEdit={onEdit} testid="byod-review-numbers">
          <Line label="Selling price" value={fmtMoney(price)} />
          <Line label={`Sales tax (${countyName} · ${(countyRow.rate * 100).toFixed(2)}%)`} value={`+ ${fmtMoney(tax)}`} />
          <Line label="Doc fee" value={`+ ${fmtMoney(FEE_DOC)}`} />
          <Line label="State fee" value={`+ ${fmtMoney(FEE_STATE)}`} />
          <Line label="Title fee" value={`+ ${fmtMoney(FEE_TITLE)}`} />
          {accessoriesTotal > 0 && <Line label="Accessories" value={`+ ${fmtMoney(accessoriesTotal)}`} />}
          {coveragesTotal > 0 && <Line label="Protection products" value={`+ ${fmtMoney(coveragesTotal)}`} />}
          {rebatesTotal > 0 && <Line label="Selected rebates" value={`− ${fmtMoney(rebatesTotal)}`} />}
          {tradeValue > 0 && <Line label="Trade value" value={`− ${fmtMoney(tradeValue)}`} />}
          {tradePayoff > 0 && <Line label="Trade payoff (added)" value={`+ ${fmtMoney(tradePayoff)}`} />}
          {down > 0 && <Line label="Cash down" value={`− ${fmtMoney(down)}`} />}
        </Section>

        {/* Disclaimer + agreement gate — customer must agree before we
            reveal the amount financed + single monthly payment. */}
        {!agreed && (
          <div className="rounded-2xl border-2 border-amber-400 bg-amber-50 p-4 sm:p-5" data-testid="byod-review-estimate-gate">
            <div className="flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-700 shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <h3 className="font-extrabold text-amber-900 leading-tight">Before we show your estimated payment</h3>
                <p className="text-sm text-amber-900 mt-2 leading-relaxed">
                  This is an <strong>estimate</strong>, not a final or approved payment. Real numbers depend
                  on the lender&rsquo;s approval, your verified credit, and the final dealer math.
                  Scott will go over the actual payment with you before anything is signed.
                </p>
                <label className="mt-3 flex items-start gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => accept(e.target.checked)}
                    className="mt-0.5"
                    data-testid="byod-review-estimate-checkbox"
                  />
                  <span className="text-sm text-amber-900 leading-snug">
                    I understand this is an estimate only — not a guaranteed monthly payment.
                  </span>
                </label>
                <button
                  type="button"
                  onClick={() => accept(true)}
                  className="ssc-btn ssc-btn-primary mt-3"
                  data-testid="byod-review-estimate-reveal"
                >
                  <Eye size={14} /> Show my estimated payment
                </button>
              </div>
            </div>
          </div>
        )}

        {agreed && (
          <div className="rounded-2xl border-2 border-[var(--ssc-navy)] bg-[var(--ssc-navy)] text-white p-5 sm:p-6" data-testid="byod-review-payment">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <div className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-[var(--ssc-yellow)]">
                Estimated monthly payment
              </div>
              <button
                type="button"
                onClick={() => accept(false)}
                className="text-[10px] font-extrabold uppercase tracking-widest text-white/70 hover:text-white flex items-center gap-1"
                data-testid="byod-review-estimate-hide"
              >
                <EyeOff size={11} /> Hide
              </button>
            </div>
            <div className="text-4xl sm:text-5xl font-black mt-1" data-testid="byod-review-monthly">
              {fmtMoney(Math.round(estimatedMonthly))}
              <span className="text-base font-bold opacity-70"> / month</span>
            </div>
            <div className="mt-4 grid sm:grid-cols-2 gap-4">
              <div>
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-yellow)]">
                  Estimated amount financed
                </div>
                <div className="text-2xl font-black mt-0.5" data-testid="byod-review-financed">
                  {fmtMoney(amountFinanced)}
                </div>
              </div>
              <div>
                <div className="text-[10px] font-extrabold uppercase tracking-widest text-[var(--ssc-yellow)]">
                  Based on
                </div>
                <ul className="text-sm mt-1 space-y-0.5" data-testid="byod-review-based-on">
                  <li>{displayTerm} months</li>
                  <li>
                    Estimated APR{aprOverride != null && aprTerm ? "" : usingDefaults ? " (default)" : ""}: {displayApr.toFixed(2)}%
                  </li>
                  <li>Approved credit{aprOverride != null && aprTerm ? " (Special APR)" : ` (${displayTierLabel})`}</li>
                  <li>Selected rebates &amp; deal options</li>
                </ul>
              </div>
            </div>
            <p className="text-xs text-white/70 mt-4 leading-snug">
              Estimate only. Final payment is set after credit approval and the actual contract.
            </p>
          </div>
        )}

        <Section title="Rate & APR" stepKey="rate" onEdit={onEdit} testid="byod-review-rate">
          {s.rate?.special_apr_id ? (
            <div className="text-sm">
              <div className="font-extrabold text-emerald-700">Special APR selected</div>
              <div className="mt-1 text-[var(--ssc-navy)]">{s.rate.special_apr_name}</div>
              <div className="text-xs text-[var(--ssc-ink-2)] mt-0.5">
                {s.rate.special_apr_pct != null ? `${Number(s.rate.special_apr_pct).toFixed(2)}% APR` : ""}
                {s.rate.special_apr_term ? ` · ${s.rate.special_apr_term} months` : ""}
              </div>
            </div>
          ) : (
            <div className="text-sm">
              <div>Credit tier: <strong>{tier.label}</strong></div>
              <div>Term: <strong>{termSelected} months</strong></div>
              <div className="text-xs text-[var(--ssc-ink-2)] mt-1">Rate is a guesstimate based on national averages. Scott confirms the real rate.</div>
            </div>
          )}
        </Section>

        <Section title="Rebates" stepKey="rebates" onEdit={onEdit} testid="byod-review-rebates">
          {(s.rebates?.selected?.length || 0) === 0 && (s.rebates?.not_sure?.length || 0) === 0
            ? <Empty text="No rebates selected." />
            : (
              <div className="space-y-2">
                {(s.rebates?.selected || []).map((r) => (
                  <div key={r.incentive_id} className="flex items-center justify-between text-sm" data-testid={`byod-review-rebate-applied-${r.incentive_id}`}>
                    <span><strong className="text-emerald-700">Applied:</strong> {r.name}</span>
                    <span className="font-bold">{r.amount ? `− ${fmtMoney(r.amount)}` : "—"}</span>
                  </div>
                ))}
                {(s.rebates?.not_sure || []).map((r) => (
                  <div key={r.incentive_id} className="flex items-center justify-between text-sm" data-testid={`byod-review-rebate-notsure-${r.incentive_id}`}>
                    <span><strong className="text-amber-700">Not sure:</strong> {r.name}</span>
                    <span className="text-xs text-[var(--ssc-ink-2)]">Scott will confirm eligibility</span>
                  </div>
                ))}
              </div>
            )}
        </Section>

        <Section title="Trade" stepKey="trade" onEdit={onEdit} testid="byod-review-trade">
          {s.trade?.has_trade === "yes" ? (
            <div className="text-sm">
              <div className="font-bold text-[var(--ssc-navy)]">{s.trade.year} {s.trade.make} {s.trade.model} {s.trade.mileage ? `· ${s.trade.mileage} mi` : ""}</div>
              <div className="mt-1">Selected trade value: <strong>{fmtMoney(tradeValue)}</strong></div>
              {tradePayoff > 0 && <div>Payoff: <strong>{fmtMoney(tradePayoff)}</strong></div>}
            </div>
          ) : s.trade?.has_trade === "no" ? <Empty text="No trade-in." />
          : s.trade?.has_trade === "not_sure" ? <Empty text="Trade — not sure yet (Scott will help)." />
          : <Empty text="Trade not answered yet." />}
        </Section>

        <Section title="Money down" stepKey="money_down" onEdit={onEdit} testid="byod-review-money-down">
          {down > 0
            ? <div className="text-sm font-bold text-[var(--ssc-navy)]">{fmtMoney(down)}</div>
            : s.money_down?.choice === "not_sure"
            ? <Empty text="Money down — not sure yet." />
            : <Empty text="$0 down." />}
        </Section>

        <Section title="Accessories" stepKey="accessories" onEdit={onEdit} testid="byod-review-accessories">
          {accessoriesTotal === 0
            ? <Empty text="No accessories added." />
            : (
              <ul className="space-y-1 text-sm">
                {(s.accessories || []).map((a, i) => (
                  <li key={a.id || i} className="flex items-center justify-between gap-2" data-testid={`byod-review-accessory-${i}`}>
                    <span>{a.name}{a.part_number ? ` · ${a.part_number}` : ""}</span>
                    <span className="font-bold">{fmtMoney(Number(a.amount) || 0)}</span>
                  </li>
                ))}
              </ul>
            )}
        </Section>

        <Section title="Required protection" stepKey="required_protection" onEdit={onEdit} testid="byod-review-required-protection">
          {(() => {
            const states = s.required_protection?.states || {};
            const accepted = Object.entries(states).filter(([, st]) => st?.status === "accepted");
            const declined = Object.entries(states).filter(([, st]) => st?.status === "declined");
            if (!accepted.length && !declined.length) return <Empty text="No decisions yet." />;
            return (
              <div className="text-sm space-y-1">
                {accepted.map(([slug, st]) => (
                  <div key={slug} className="flex items-center justify-between gap-2">
                    <span><strong className="text-emerald-700">Accepted:</strong> {slugLabel(slug)}</span>
                    <span className="font-bold">{fmtMoney(Number(st.finalPrice) || 0)}</span>
                  </div>
                ))}
                {declined.map(([slug]) => (
                  <div key={slug} className="text-[var(--ssc-ink-2)]">
                    <strong>Declined:</strong> {slugLabel(slug)}
                  </div>
                ))}
              </div>
            );
          })()}
        </Section>

        <Section title="Optional protection" stepKey="optional_protection" onEdit={onEdit} testid="byod-review-optional-protection">
          {(() => {
            const states = s.optional_protection?.states || {};
            const accepted = Object.entries(states).filter(([, st]) => st?.status === "accepted");
            const declined = Object.entries(states).filter(([, st]) => st?.status === "declined");
            if (!accepted.length && !declined.length) return <Empty text="No optional add-ons selected." />;
            return (
              <div className="text-sm space-y-1">
                {accepted.map(([slug, st]) => (
                  <div key={slug} className="flex items-center justify-between gap-2">
                    <span><strong className="text-emerald-700">Accepted:</strong> {slugLabel(slug)}</span>
                    <span className="font-bold">{fmtMoney(Number(st.finalPrice) || 0)}</span>
                  </div>
                ))}
                {declined.map(([slug]) => (
                  <div key={slug} className="text-[var(--ssc-ink-2)]">
                    <strong>Declined:</strong> {slugLabel(slug)}
                  </div>
                ))}
              </div>
            );
          })()}
        </Section>
      </div>
    </StepShell>
  );
}

function slugLabel(slug) {
  return String(slug || "").replace(/-/g, " ").replace(/\b\w/g, (m) => m.toUpperCase());
}

function Section({ title, stepKey, onEdit, children, testid }) {
  return (
    <div className="rounded-2xl border-2 border-[var(--ssc-line)] bg-white" data-testid={testid}>
      <div className="flex items-center justify-between gap-2 px-4 sm:px-5 py-3 border-b border-[var(--ssc-line)]">
        <h3 className="font-extrabold text-[var(--ssc-navy)]">{title}</h3>
        <button
          type="button"
          onClick={() => onEdit?.(stepKey)}
          className="text-xs font-bold text-[var(--ssc-blue)] hover:underline flex items-center gap-1"
          data-testid={`${testid}-edit`}
        >
          <Pencil size={12} /> Edit
        </button>
      </div>
      <div className="p-4 sm:p-5 space-y-1.5">{children}</div>
    </div>
  );
}

function Line({ label, value, bold }) {
  return (
    <div className={`flex items-center justify-between gap-2 ${bold ? "font-extrabold text-[var(--ssc-navy)] border-t border-[var(--ssc-line)] pt-2 mt-1" : "text-sm text-[var(--ssc-ink-2)]"}`}>
      <span>{label}</span><span className="tabular-nums">{value}</span>
    </div>
  );
}

function Empty({ text }) {
  return <p className="text-sm text-[var(--ssc-ink-2)] italic">{text}</p>;
}
